import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Search, Menu, X, ArrowRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import GlobalSearch from "@/components/GlobalSearch";
import { HEADER_NAV, isHidden } from "@/lib/siteNav";

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.3 });
  return <motion.div style={{ scaleX }} className="absolute inset-x-0 bottom-0 h-[2px] origin-left bg-npst-gold/80" aria-hidden="true" />;
}

export default function Header() {
  const settings = useSettings();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV = HEADER_NAV.filter((n) => !isHidden(settings, n.id));

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (to) => to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <header className={`sticky top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${scrolled ? "bg-npst-bg/90 backdrop-blur-xl border-npst-line shadow-[0_6px_24px_-18px_rgba(15,41,74,0.35)]" : "bg-npst-bg/95 backdrop-blur-md border-transparent"}`} data-testid="site-header">
        {!isHidden(settings, "header-topbar") && (
        <div className="hidden md:block border-b border-npst-line/70">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-8 text-[11px] tracking-wide text-npst-muted font-sans">
            <span className="label-eyebrow text-npst-gold">Public Interest · Institutional Accountability</span>
            <div className="flex items-center gap-4">
              {settings.email && <a href={`mailto:${settings.email}`} className="hover:text-npst-navy transition-colors">{settings.email}</a>}
              <Link to="/admin" className="hover:text-npst-navy transition-colors" data-testid="admin-portal-link">Admin Portal</Link>
            </div>
          </div>
        </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className={`flex items-center justify-between gap-3 transition-[height] duration-300 ${scrolled ? "h-[64px]" : "h-[72px]"}`}>
            <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="logo-link">
              <img src={settings.logo || "/npst-logo.webp"} alt="NPST emblem" className="w-10 h-10 sm:w-11 sm:h-11 object-contain shrink-0 transition-transform duration-500 group-hover:rotate-[8deg]" />
              <div className="leading-tight min-w-0">
                <div className="font-display text-[12px] min-[400px]:text-[13px] sm:text-base font-bold tracking-wide text-npst-navy">NATIONAL PUBLIC SERVICE TRUST</div>
                <div className="text-[8px] min-[400px]:text-[9px] sm:text-[10px] tracking-[0.14em] sm:tracking-[0.25em] uppercase text-npst-gold font-semibold">Public Welfare · Justice · Development</div>
              </div>
            </Link>

            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => setSearchOpen(true)} data-testid="open-search-btn" aria-label="Search"
                className="h-10 flex items-center gap-2 rounded-full border border-transparent px-2.5 text-npst-ink transition-colors hover:border-npst-goldBorder hover:bg-npst-goldLight">
                <Search size={18} />
                <span className="hidden xl:inline font-mono text-[10px] text-npst-muted border border-npst-line rounded px-1.5 py-0.5">Ctrl K</span>
              </button>
              <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" aria-label="Menu" aria-expanded={mobileOpen}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full text-npst-ink transition-colors hover:bg-npst-goldLight">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span key={mobileOpen ? "x" : "m"} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                  </motion.span>
                </AnimatePresence>
              </button>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-1 h-11 border-t border-npst-line/70 overflow-x-auto no-scrollbar" data-testid="desktop-nav">
            {NAV.map((n) => {
              const active = isActive(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={`relative shrink-0 px-2.5 py-2 text-[12.5px] font-sans font-medium tracking-wide rounded-md transition-colors ${active ? "text-npst-navy" : "text-npst-inkSoft hover:text-npst-navy hover:bg-npst-goldLight/70"}`}>
                  {n.label}
                  {active && <motion.span layoutId="nav-active" className="absolute left-2.5 right-2.5 -bottom-[5px] h-[2px] rounded-full bg-npst-gold" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                </Link>
              );
            })}
          </nav>
        </div>
        <ScrollProgress />

        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.215, 0.61, 0.355, 1] }}
              className="lg:hidden overflow-hidden border-t border-npst-line bg-npst-bg" data-testid="mobile-nav">
              <nav className="px-4 py-3 flex flex-col max-h-[calc(100vh-80px)] overflow-y-auto">
                {NAV.map((n, i) => (
                  <motion.div key={n.to} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.03 * i }}>
                    <Link to={n.to} className={`flex items-center justify-between py-3 border-b border-npst-line/60 text-[15px] font-sans transition-colors ${isActive(n.to) ? "text-npst-navy font-semibold" : "text-npst-inkSoft hover:text-npst-navy"}`}>
                      <span className="flex items-center gap-3">
                        <span className={`h-1.5 w-1.5 rounded-full ${isActive(n.to) ? "bg-npst-gold" : "bg-npst-line"}`} />{n.label}
                      </span>
                      <ArrowRight size={15} className="text-npst-gold/70" />
                    </Link>
                  </motion.div>
                ))}
                <Link to="/admin" className="py-3 text-[13px] label-eyebrow text-npst-gold">Admin Portal →</Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
